import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { ConsoleApp } from './ConsoleApp.js';
import { Spinner } from './components/ui/index.js';

/*
 * THE PUBLIC ROUTES ARE MATCHED BEFORE ANYTHING READS THE SESSION, AND THAT
 * ORDERING IS THE ENTIRE POINT OF THIS FILE.
 *
 * `/legal/*` is submitted to Google Play and App Store Connect — the privacy
 * policy, the account-deletion page, the support page and the terms. A store
 * reviewer opens those URLs signed out, on a phone, sometimes on a connection
 * that cannot reach `api.fieldvision.id` at all. Every one of the console's
 * gates — session bootstrap, `GET /auth/me`, the console-membership test, the
 * `path="*"` redirect to the landing page — used to sit ABOVE the router, so a
 * signed-out visitor to `/legal/privacy` was served a login form and a signed-in
 * one was redirected to `/overview`. Both are a failed submission.
 *
 * THIS COMPONENT MUST NEVER CALL A HOOK. That is what keeps the split safe: the
 * branch below is a ROUTE MATCH, so React unmounts one component and mounts the
 * other, and hook order is a property of each rather than of a shared render. An
 * `if (pathname.startsWith('/legal'))` inside the old single component cannot
 * achieve that — placed above the hooks it changes their count between renders
 * and React throws "Rendered more hooks than during the previous render"; placed
 * below them, `useSessionBootstrap` has already fired `POST /auth/refresh` and
 * `useAccessScope` has already queued `GET /auth/me`, which is the exact
 * property being bought.
 *
 * LAZY, like every other non-landing route (see the note in `ConsoleApp.tsx`):
 * four documents of long prose belong nowhere near the entry chunk. A lazy
 * import is a same-origin chunk fetch — no token, no API, no session — so it
 * does not weaken any of the above.
 */
const LegalRoutes = lazy(() =>
  import('./pages/legal/LegalRoutes.js').then((module) => ({ default: module.LegalRoutes })),
);

/*
 * `/panduan/*` — the published user guides, and public for a different reason
 * than `/legal/*` is.
 *
 * Nobody submits these to a store. They are read by the people the console is
 * NOT for: a salesperson mid-shift, on a handset, following a WhatsApp link from
 * their supervisor, holding an account that would be bounced off every console
 * route by the membership test — and often standing somewhere the API cannot be
 * reached at all. A guide that answers "why is my Check-out button grey" behind
 * a login is a guide that is not there at the moment it is needed.
 *
 * So it sits above the session gates for exactly the reason `/legal/*` does, and
 * inherits the same import discipline: see the note in `pages/guide/GuideRoutes.tsx`.
 */
const GuideRoutes = lazy(() =>
  import('./pages/guide/GuideRoutes.js').then((module) => ({ default: module.GuideRoutes })),
);

export function App() {
  return (
    <Routes>
      <Route
        path="/legal/*"
        element={
          <Suspense
            fallback={
              <div className="flex min-h-dvh items-center justify-center p-gutter">
                <Spinner label="Memuat…" />
              </div>
            }
          >
            <LegalRoutes />
          </Suspense>
        }
      />
      <Route
        path="/panduan/*"
        element={
          <Suspense
            fallback={
              <div className="flex min-h-dvh items-center justify-center p-gutter">
                <Spinner label="Memuat…" />
              </div>
            }
          >
            <GuideRoutes />
          </Suspense>
        }
      />
      {/*
        Everything else, exactly as before. A splat route's `pathnameBase`
        collapses to "/", so the descendant <Routes> inside ConsoleApp still
        matches its absolute paths ("/overview", "/master/skus") unchanged — and
        its own `path="*"` catch-all still works, now scoped to non-/legal URLs.
      */}
      <Route path="*" element={<ConsoleApp />} />
    </Routes>
  );
}
